import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {activeStories, saveStories, storyMediaURL, STORY_LIFETIME} from '../lib/stories-service.js';

export default function Stories({tinode, contacts, ownerId, author, connected}) {
  const [revision, setRevision] = useState(0);
  const [compose, setCompose] = useState(false);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState('');
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [viewer, setViewer] = useState(null);
  const [paused, setPaused] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [progress, setProgress] = useState(0);
  const mounted = useRef(true);
  const uploader = useRef(null);
  const input = useRef(null);
  const video = useRef(null);
  useEffect(() => {mounted.current = true; return () => {mounted.current = false; uploader.current?.cancel();};}, []);
  useEffect(() => {const timer = setInterval(() => setRevision(v=>v+1),30000);return ()=>clearInterval(timer);},[]);
  useEffect(() => {
    if(!file){setPreview('');return;}
    const url = URL.createObjectURL(file);setPreview(url);return ()=>URL.revokeObjectURL(url);
  },[file]);
  const me = tinode.getMeTopic();
  const own = activeStories(me.public?.vibeStories);
  // Remove expired entries from the owner's profile when they next connect.
  useEffect(() => {
    const rows = me.public?.vibeStories;
    if(!connected || busy || !Array.isArray(rows) || !rows.some(row => row?.expiresAt <= Date.now())) return;
    saveStories(tinode, activeStories(rows)).then(()=>{if(mounted.current)setRevision(v=>v+1);}).catch(()=>{});
  },[connected,revision,busy]);
  const people = [{id:ownerId,name:author || 'Моя история',stories:own}];
  for (const c of contacts || []) {
    if (!c.topic?.startsWith('usr') || c.topic===ownerId || (c.acs && !c.acs.isJoiner()) || c.private?.arch) continue;
    const stories = activeStories(c.public?.vibeStories);
    if(stories.length) people.push({id:c.topic,name:c.public?.fn || 'Контакт',stories});
  }
  const advance = direction => setViewer(current => {
    if(!current) return null;
    const next=current.index+direction;
    if(next<0 || next>=current.person.stories.length) return null;
    return {...current,index:next};
  });
  const current = viewer?.person.stories[viewer.index];
  useEffect(()=>{if(current && current.expiresAt <= Date.now())setViewer(null);},[revision,current?.id]);
  useEffect(() => {
    setProgress(0);setPaused(false);setError('');setLoaded(current?.type === 'text');
  },[current?.id]);
  useEffect(() => {
    if(!viewer) return;
    const handler = e => {if(e.key==='Escape')setViewer(null);if(e.key==='ArrowRight')advance(1);if(e.key==='ArrowLeft')advance(-1);};
    document.addEventListener('keydown',handler);return ()=>document.removeEventListener('keydown',handler);
  },[viewer]);
  useEffect(() => {
    if(!current || paused || !loaded || current.type==='video') return;
    const timer=setInterval(()=>{if(!document.hidden)setProgress(p=>p+100/80);},100);
    return ()=>clearInterval(timer);
  },[current?.id,paused,loaded]);
  useEffect(()=>{if(progress>=100)advance(1);},[progress]);
  useEffect(()=>{if(video.current){if(paused)video.current.pause();else video.current.play().catch(()=>{});}},[paused,current?.id]);
  useEffect(()=>{const hide=()=>{if(document.hidden){setPaused(true);video.current?.pause();}};document.addEventListener('visibilitychange',hide);return()=>document.removeEventListener('visibilitychange',hide);},[]);

  const refresh = async () => {
    setError('');
    try{await me.getMeta(me.startMetaQuery().withDesc().withSub().build());setRevision(v=>v+1);}
    catch{setError('Не удалось обновить истории. Проверьте подключение.');}
  };
  const publish = async () => {
    if(busy || !connected)return;
    setBusy(true);setError('');
    try {
      if(own.length>=8)throw new Error('Можно опубликовать до 8 историй. Удалите одну из своих историй.');
      let media = {};
      if(file){
        if(!/^(image|video)\//.test(file.type))throw new Error('Выберите фото или видео.');
        const limit=Math.min(tinode.getServerParam('maxFileUploadSize',8*1024*1024),20*1024*1024);
        if(file.size>limit)throw new Error(`Файл слишком большой. Максимум ${Math.floor(limit/1024/1024)} МБ.`);
        if(file.type.startsWith('video/')) {
          await new Promise((resolve,reject)=>{const v=document.createElement('video');const url=URL.createObjectURL(file);const done=()=>{v.removeAttribute('src');v.load();URL.revokeObjectURL(url);};v.onloadedmetadata=()=>{const ok=Number.isFinite(v.duration)&&v.duration<=60;done();ok?resolve():reject(new Error('История может содержать видео до 60 секунд.'));};v.onerror=()=>{done();reject(new Error('Не удалось прочитать видео.'));};v.src=url;});
        }
        const helper=uploader.current=tinode.getLargeFileHelper();
        if(!helper)throw new Error('Загрузка недоступна.');
        const ref=await helper.upload(file);
        media={type:file.type.startsWith('video/')?'video':'image',ref};
      }else{
        if(!caption.trim())throw new Error('Добавьте фото, видео или текст.');
        media={type:'text',text:caption.trim().slice(0,500)};
      }
      const createdAt=Date.now();
      await saveStories(tinode,[...activeStories(me.public?.vibeStories),{id:crypto.randomUUID(),createdAt,expiresAt:createdAt+STORY_LIFETIME,caption:file?caption.trim().slice(0,500):'',...media}]);
      if(mounted.current){setCompose(false);setFile(null);setCaption('');setRevision(v=>v+1);}
    }catch(err){if(mounted.current)setError(err.message || 'Не удалось опубликовать историю.');}
    finally{uploader.current=null;if(mounted.current)setBusy(false);}
  };
  const remove = async () => {
    if(!current || busy)return;
    setBusy(true);
    setError('');
    try{await saveStories(tinode,activeStories(me.public?.vibeStories).filter(row=>row.id!==current.id));setViewer(null);setRevision(v=>v+1);}
    catch(err){setError(err.message);}
    finally{setBusy(false);}
  };
  return <section className="vibe-stories" aria-label="Истории" data-revision={revision}>
    <div className="vibe-stories-heading"><span>Истории</span><button onClick={refresh} disabled={!connected} aria-label="Обновить истории">↻</button></div>
    <div className="vibe-stories-strip"><button className="vibe-story-person" disabled={!connected} onClick={()=>{setError('');setCompose(true);}}><span className="vibe-story-ring vibe-story-add">＋</span><span>Добавить</span></button>
      {people.filter(p=>p.stories.length).map(p=><button className="vibe-story-person" key={p.id} onClick={()=>setViewer({person:p,index:0})}><span className="vibe-story-ring">{p.name.split(/\s+/).slice(0,2).map(n=>n[0]).join('')}</span><span>{p.id===ownerId?'Моя история':p.name}</span></button>)}
    </div>
    {error && !compose && !viewer && <p role="alert" className="vibe-media-error">{error}</p>}
    {compose && createPortal(<div className="vibe-media-overlay"><section className="vibe-media-dialog" role="dialog" aria-modal="true" aria-label="Новая история">
      <header><h2>Новая история</h2><button autoFocus disabled={busy} onClick={()=>{setCompose(false);setFile(null);}} aria-label="Закрыть">×</button></header>
      <p>На 24 часа · в вашем профиле</p><p className="vibe-story-audience">Историю смогут увидеть пользователи с доступом к вашему профилю. Это не личное сообщение.</p>
      {file && <div className="vibe-story-composer-preview">{file.type.startsWith('video/')?<video src={preview} controls playsInline />:<img src={preview} alt="Предпросмотр истории" />}</div>}
      <input ref={input} type="file" accept="image/*,video/*" hidden onChange={e=>{setFile(e.target.files[0] || null);e.target.value='';}} />
      <button disabled={busy} onClick={()=>input.current.click()}>{file?'Заменить фото / видео':'Добавить фото / видео'}</button>
      <label className="vibe-story-caption">{file?'Подпись':'Текст истории'}<textarea maxLength={500} value={caption} disabled={busy} onChange={e=>setCaption(e.target.value)} placeholder="Что нового?" /></label>
      {error && <p role="alert" className="vibe-media-error">{error}</p>}
      <footer><button className="vibe-media-primary" disabled={busy || !connected || (!file && !caption.trim())} onClick={publish}>{busy?'Публикуем…':'Опубликовать'}</button></footer>
    </section></div>,document.body)}
    {current && createPortal(<div className="vibe-media-overlay"><section className="vibe-story-viewer" role="dialog" aria-modal="true" aria-label={`История: ${viewer.person.name}`}>
      <div className="vibe-story-progress">{viewer.person.stories.map((s,i)=><span key={s.id}><i style={{width:(i<viewer.index?100:i===viewer.index?Math.min(progress,100):0)+'%'}} /></span>)}</div>
      <header><b>{viewer.person.name}</b><button onClick={()=>setPaused(!paused)} aria-label={paused?'Продолжить':'Пауза'}>{paused?'▶':'Ⅱ'}</button><button autoFocus onClick={()=>setViewer(null)} aria-label="Закрыть">×</button></header>
      <div className="vibe-story-media">{current.type==='text'?<p>{current.text}</p>:current.type==='video'?<video key={current.id} ref={video} src={storyMediaURL(tinode,current)} playsInline autoPlay controls onTimeUpdate={e=>{if(e.target.duration)setProgress(e.target.currentTime/e.target.duration*100);}} onEnded={()=>advance(1)} onError={()=>{setPaused(true);setError('Не удалось загрузить видео.');}} />:<img src={storyMediaURL(tinode,current)} alt={current.caption || 'История'} onLoad={()=>setLoaded(true)} onError={()=>{setPaused(true);setError('Не удалось загрузить фото.');}} />}</div>
      {current.caption && <p className="vibe-story-viewer-caption">{current.caption}</p>}
      {error && <p role="alert">{error}</p>}
      <footer><button onClick={()=>advance(-1)} aria-label="Предыдущая история">←</button><span>24 часа</span>{viewer.person.id===ownerId && <button disabled={busy} onClick={remove}>Удалить</button>}<button onClick={()=>advance(1)} aria-label="Следующая история">→</button></footer>
    </section></div>,document.body)}
  </section>;
}
